// UserDashboard.jsx - Complete Enhanced Dashboard with Real Usage Integration
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { useSearch } from '../utils/searchStorage';
import { apiClient } from '../utils/api';
import ClientAnalyticsDashboard from './ClientAnalyticsDashboard';

import { 
  Search, 
  Zap, 
  ArrowRight, 
  TrendingUp, 
  Eye, 
  Brain, 
  Target, 
  CheckCircle, 
  Clock,
  Globe,
  Users,
  Activity,
  BarChart3,
  Sparkles,
  Star,
  Calendar,
  Award,
  AlertTriangle,
  Plus,
  ExternalLink,
  Wifi,
  WifiOff,
  Share2,
  RefreshCw,
  Gauge,
  TrendingDown,
  AlertCircle,
  ChevronRight,
  LineChart
} from "lucide-react";
import CompactReportModalWithShare from './CompactReportModalWithShare.jsx';

const UserDashboard = () => {
  const { user, getCurrentTier, getTierConfig, checkUsageLimit, getUpgradeRecommendation } = useAuth();
  const { search } = useSearch();
  const navigate = useNavigate();
  
  // Core state
  const [quickSearchUrl, setQuickSearchUrl] = useState('');
  const [searchStats, setSearchStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState('checking');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  
  // Real usage data state
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);
  const [refreshingUsage, setRefreshingUsage] = useState(false);

  // Get user data safely
  const currentTier = getCurrentTier ? getCurrentTier() : 'free';
  const tierConfig = getTierConfig ? getTierConfig() : { name: 'Free', limits: { searches: 10 }, price: 0 };
  const upgradeRec = getUpgradeRecommendation ? getUpgradeRecommendation() : null;

  // Fetch real usage data from backend
  const fetchUsageData = async (showRefreshLoader = false) => {
    if (!user) {
      console.log('👤 No user, skipping usage data fetch');
      setUsageLoading(false);
      return;
    }

    try {
      if (showRefreshLoader) setRefreshingUsage(true);
      else setUsageLoading(true);
      
      setUsageError(null);
      
      console.log('📊 Fetching real usage data...');
      const data = await apiClient.getUserUsage();
      
      console.log('✅ Usage data received:', data);
      setUsageData(data);
      
    } catch (error) {
      console.error('❌ Failed to fetch usage data:', error);
      setUsageError(error.message);
      
      // Fallback to estimated data based on tier
      const fallbackLimits = {
        free: { website_analyses: 10, api_calls: 50, advanced_scans: 0, exports: 5 },
        starter: { website_analyses: 100, api_calls: 500, advanced_scans: 10, exports: 50 },
        pro: { website_analyses: 1000, api_calls: 5000, advanced_scans: 100, exports: 500 },
        enterprise: { website_analyses: -1, api_calls: -1, advanced_scans: -1, exports: -1 }
      };
      
      setUsageData({
        current_period: {
          website_analyses: searchStats.thisMonth || 0,
          api_calls: Math.floor((searchStats.thisMonth || 0) * 2.5),
          advanced_scans: 0,
          exports: 0,
          total_analyses: searchStats.thisMonth || 0
        },
        limits: fallbackLimits[currentTier] || fallbackLimits.free,
        daily_chart_data: [],
        user_tier: currentTier,
        period_start: new Date().toISOString(),
        period_end: new Date(Date.now() + 30*24*60*60*1000).toISOString()
      });
    } finally {
      setUsageLoading(false);
      setRefreshingUsage(false);
    }
  };
// Enhanced Client Analytics Dashboard Component
  const Dashboard = ({ initialView = 'dashboard' }) => {
    const [activeView, setActiveView] = useState(initialView);
  // Add 'analytics' to your view options
  const views = {
    dashboard: <YourCurrentDashboardContent />,
    reviews: <ReviewsContent />,
    analytics: <ClientAnalyticsDashboard />, // 🔥 ADD THIS
    // ... other views
  };
  
  // Add analytics to your navigation tabs
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp }, // 🔥 ADD THIS
    // ... other nav items
  ];
  
  return (
    <div className="dashboard">
      {/* Navigation */}
      <nav className="dashboard-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveView(item.id)}
            className={`nav-item ${activeView === item.id ? 'active' : ''}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </button>
        ))}
      </nav>
      
      {/* Content */}
      <div className="dashboard-content">
        {views[activeView]}
      </div>
    </div>
  );
};  

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Get search statistics
        if (search && search.getSearchStats) {
          const stats = search.getSearchStats();
          setSearchStats(stats);
        }

        // Check API status
        try {
          await apiClient.checkHealth();
          setApiStatus('online');
        } catch (error) {
          setApiStatus('offline');
        }

        // Fetch real usage data
        await fetchUsageData();

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [search, user]);

  // Periodic usage refresh
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      console.log('⏰ Periodic usage refresh...');
      fetchUsageData(true);
    }, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(interval);
  }, [user]);

  // Get recent searches safely
  const recentSearches = useMemo(() => {
    if (!search || !search.getRecentSearches) return [];
    try {
      return search.getRecentSearches(5);
    } catch (error) {
      console.warn('Error getting recent searches:', error);
      return [];
    }
  }, [search]);

  // Calculate usage info from real data
  const usageInfo = useMemo(() => {
    if (!usageData) {
      return { used: 0, limit: 10, remaining: 10, canUse: true, percentage: 0 };
    }

    const used = usageData.current_period?.website_analyses || 0;
    const limit = usageData.limits?.website_analyses || 10;
    const remaining = limit === -1 ? -1 : Math.max(0, limit - used);
    const percentage = limit === -1 ? 0 : Math.min(100, (used / limit) * 100);
    
    return {
      used,
      limit,
      remaining,
      canUse: remaining > 0 || remaining === -1,
      percentage
    };
  }, [usageData]);

  // Quick search handler
  const handleQuickSearch = (e) => {
    e.preventDefault();
    if (quickSearchUrl.trim()) {
      navigate('/search', { 
        state: { 
          prefill: quickSearchUrl.trim(),
          source: 'dashboard'
        } 
      });
      setQuickSearchUrl('');
    } else {
      navigate('/search');
    }
  };

  // Handle share analysis
  const handleShareAnalysis = (analysis) => {
    setSelectedAnalysis(analysis);
    setShowDetailModal(true);
  };

  // Manual refresh usage
  const handleRefreshUsage = () => {
    fetchUsageData(true);
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  // Get usage color based on percentage
  const getUsageColor = (percentage) => {
    if (percentage >= 90) return { bg: 'bg-red-500', text: 'text-red-400', border: 'border-red-500/30' };
    if (percentage >= 75) return { bg: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/30' };
    if (percentage >= 50) return { bg: 'bg-yellow-500', text: 'text-yellow-400', border: 'border-yellow-500/30' };
    return { bg: 'bg-green-500', text: 'text-green-400', border: 'border-green-500/30' };
  };

  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const date = new Date(timestamp);
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Enhanced stats cards with real data
  const statsCards = [
    {
      title: 'Analyses This Month',
      value: usageData?.current_period?.website_analyses || searchStats.thisMonth || 0,
      subtitle: `${usageData?.current_period?.total_analyses || searchStats.total || 0} total`,
      icon: Brain,
      color: 'blue',
      trend: (usageData?.current_period?.website_analyses || 0) > 0 ? 'Active' : null,
      loading: usageLoading
    },
    {
      title: 'Average Score',
      value: searchStats.averageScore || 0,
      subtitle: 'Across all sites',
      icon: Target,
      color: 'green',
      trend: searchStats.averageScore >= 85 ? 'Excellent' : searchStats.averageScore >= 70 ? 'Good' : null,
      loading: false
    },
    {
      title: 'API Calls',
      value: usageData?.current_period?.api_calls || 0,
      subtitle: usageData?.limits?.api_calls === -1 ? 'Unlimited' : `${usageData?.limits?.api_calls || 50} limit`,
      icon: Activity,
      color: 'purple',
      trend: (usageData?.current_period?.api_calls || 0) > 0 ? '+15%' : null,
      loading: usageLoading
    },
    {
      title: 'Plan Usage',
      value: usageInfo.remaining === -1 ? '∞' : `${usageInfo.remaining}`,
      subtitle: `${usageInfo.percentage.toFixed(0)}% used`,
      icon: Gauge,
      color: usageInfo.percentage >= 90 ? 'red' : usageInfo.percentage >= 75 ? 'orange' : 'cyan',
      trend: usageInfo.percentage >= 90 ? 'High' : usageInfo.percentage >= 75 ? 'Medium' : 'Low',
      loading: usageLoading
    }
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-12 bg-slate-700/50 rounded-xl w-1/2 mb-4"></div>
            <div className="h-6 bg-slate-800/50 rounded-lg w-1/3 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-slate-800/50 rounded-xl p-6 h-32"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 bg-slate-800/50 rounded-xl p-6 h-64"></div>
              <div className="bg-slate-800/50 rounded-xl p-6 h-64"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-black mb-2">
                <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                  {getGreeting()}, {user?.name || 'User'}!
                </span>
              </h1>
              <p className="text-xl text-slate-300">
                Welcome to your AI-powered website intelligence dashboard
              </p>
            </div>
            
            {/* Status Indicators */}
            <div className="hidden md:flex flex-col items-end gap-3">
              {/* API Status */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                apiStatus === 'online' 
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
              }`}>
                {apiStatus === 'online' ? (
                  <>
                    <Wifi className="w-4 h-4" />
                    <span>AI Online</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-4 h-4" />
                    <span>Demo Mode</span>
                  </>
                )}
              </div>
              
              {/* Tier Badge */}
              <div className={`px-4 py-2 rounded-xl text-sm font-bold border ${
                currentTier === 'free' 
                  ? 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                  : currentTier === 'starter'
                  ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                  : currentTier === 'pro'
                  ? 'bg-green-500/20 text-green-300 border-green-500/30'
                  : 'bg-purple-500/20 text-purple-300 border-purple-500/30'
              }`}>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  <span>{tierConfig.name}</span>
                  {tierConfig.price > 0 && (
                    <span className="opacity-75">${tierConfig.price}/mo</span>
                  )}
                </div>
              </div>
              
              {/* Usage Quick View */}
              <div className={`px-4 py-2 rounded-xl text-xs font-medium border ${getUsageColor(usageInfo.percentage).border} ${getUsageColor(usageInfo.percentage).text} bg-opacity-20`}>
                <div className="flex items-center gap-2">
                  <Gauge className="w-3 h-3" />
                  <span>{usageInfo.remaining === -1 ? 'Unlimited' : `${usageInfo.remaining} left`}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade Recommendation */}
          {upgradeRec && (
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6 mb-6 backdrop-blur-sm">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-blue-300 font-bold text-lg">{upgradeRec.reason}</h3>
                    <p className="text-blue-200 mt-1">{upgradeRec.message}</p>
                  </div>
                </div>
                <Link 
                  to="/pricing" 
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-bold transition-all transform hover:scale-105"
                >
                  <Sparkles className="w-5 h-5" />
                  Upgrade Now
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Quick Search */}
        <div className="mb-8">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Search className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Quick Website Analysis</h2>
                  <p className="text-slate-400">Analyze any website with our AI-powered multi-agent system</p>
                </div>
              </div>
              
              <form onSubmit={handleQuickSearch} className="flex gap-4">
                <div className="flex-1 relative">
                  <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-slate-400">
                    <Globe className="w-6 h-6" />
                  </div>
                  <input
                    type="text"
                    value={quickSearchUrl}
                    onChange={(e) => setQuickSearchUrl(e.target.value)}
                    placeholder="Enter website URL (e.g., apple.com, github.com)..."
                    className="w-full pl-14 pr-6 py-5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg"
                  />
                </div>
                <button
                  type="submit"
                  className="px-8 py-5 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <Brain className="w-6 h-6" />
                    <span>Analyze</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </button>
              </form>
              
              <div className="mt-4 text-center">
                <Link 
                  to="/search" 
                  className="text-blue-400 hover:text-blue-300 text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <span>Advanced search interface</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div key={index} className="group relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-600 to-slate-700 rounded-2xl blur opacity-0 group-hover:opacity-50 transition duration-300"></div>
                <div className="relative bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-2xl p-6 hover:border-slate-600/50 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-4 rounded-xl bg-gradient-to-r shadow-lg ${
                      stat.color === 'blue' ? 'from-blue-500/20 to-blue-600/20 shadow-blue-500/20' :
                      stat.color === 'green' ? 'from-green-500/20 to-green-600/20 shadow-green-500/20' :
                      stat.color === 'purple' ? 'from-purple-500/20 to-purple-600/20 shadow-purple-500/20' :
                      stat.color === 'cyan' ? 'from-cyan-500/20 to-cyan-600/20 shadow-cyan-500/20' :
                      stat.color === 'orange' ? 'from-orange-500/20 to-orange-600/20 shadow-orange-500/20' :
                      'from-red-500/20 to-red-600/20 shadow-red-500/20'
                    }`}>
                      {stat.loading ? (
                        <div className="w-7 h-7 border-2 border-current border-t-transparent rounded-full animate-spin opacity-50"></div>
                      ) : (
                        <IconComponent className={`w-7 h-7 ${
                          stat.color === 'blue' ? 'text-blue-400' :
                          stat.color === 'green' ? 'text-green-400' :
                          stat.color === 'purple' ? 'text-purple-400' :
                          stat.color === 'cyan' ? 'text-cyan-400' :
                          stat.color === 'orange' ? 'text-orange-400' :
                          'text-red-400'
                        }`} />
                      )}
                    </div>
                    {stat.trend && (
                      <div className={`px-3 py-1 rounded-lg text-xs font-bold ${
                        stat.trend.includes('%') || stat.trend === 'Excellent' || stat.trend === 'Active' || stat.trend === 'Good' ? 'bg-green-500/20 text-green-400' : 
                        stat.trend === 'High' || stat.trend === 'Medium' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {stat.trend}
                      </div>
                    )}
                  </div>
                  <h3 className="text-slate-400 text-sm font-medium mb-2">{stat.title}</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-white text-3xl font-black">{stat.value}</p>
                    {typeof stat.value === 'number' && stat.value > 0 && (
                      <TrendingUp className="w-4 h-4 text-green-400" />
                    )}
                  </div>
                  <p className="text-slate-500 text-xs mt-1">{stat.subtitle}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Recent Analyses */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Recent Analyses</h2>
                    <p className="text-slate-400 text-sm">Your latest website intelligence reports</p>
                  </div>
                </div>
                <Link 
                  to="/reviews" 
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-all group"
                >
                  <span>View All</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {recentSearches.length > 0 ? (
                <div className="space-y-4">
                  {recentSearches.map((analysis, index) => (
                    <div key={analysis.id || index} className="group relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                      <div className="relative bg-slate-900/60 border border-slate-700/50 rounded-xl p-6 hover:border-slate-600/50 transition-all transform hover:scale-[1.02]">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-3">
                              <h3 className="text-white font-bold text-lg truncate">{analysis.url}</h3>
                              {analysis.demo_mode && (
                                <span className="bg-yellow-500/20 text-yellow-300 px-3 py-1 rounded-full text-xs font-medium border border-yellow-500/30">
                                  Demo
                                </span>
                              )}
                              <div className={`text-2xl font-black ${getScoreColor(analysis.score)}`}>
                                {analysis.score}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatTimeAgo(analysis.timestamp)}
                              </span>
                              {analysis.analysis_time && (
                                <span className="flex items-center gap-1">
                                  <Zap className="w-4 h-4" />
                                  {analysis.analysis_time}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <button 
                              onClick={() => handleShareAnalysis(analysis)}
                              className="bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white px-4 py-3 rounded-xl text-sm font-medium transition-all border border-green-600/30 hover:border-green-600"
                              title="View Details & Share"
                            >
                              <Share2 className="w-5 h-5" />
                            </button>
                            
                            <button 
                              onClick={() => navigate(`/report/${analysis.id}`, { state: { analysis } })}
                              className="bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white px-6 py-3 rounded-xl font-medium transition-all border border-blue-600/30 hover:border-blue-600 flex items-center gap-2"
                            >
                              <Eye className="w-5 h-5" />
                              <span>View Report</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-slate-300 font-bold text-lg mb-3">No analyses yet</h3>
                  <p className="text-slate-400 mb-6">Start by analyzing your first website with our AI system</p>
                  <button 
                    onClick={() => navigate('/search')}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-bold transition-all transform hover:scale-105 flex items-center gap-3 mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    Analyze First Website
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Usage Overview */}
          <div className="space-y-6">
            <div className="bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Usage Overview</h2>
                </div>
                
                <button 
                  onClick={handleRefreshUsage}
                  disabled={refreshingUsage}
                  className="p-2 text-slate-400 hover:text-white transition-all hover:bg-slate-700/50 rounded-lg"
                  title="Refresh usage data"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingUsage ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              {usageError && (
                <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-center gap-2 text-yellow-300 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>Using estimated data - {usageError}</span>
                  </div>
                </div>
              )}
              
              <div className="space-y-6">
                {/* Website Analyses */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-slate-400 font-medium">Website Analyses</span>
                    <span className="text-white font-bold">
                      {usageData?.current_period?.website_analyses || 0} / {
                        usageData?.limits?.website_analyses === -1 ? '∞' : (usageData?.limits?.website_analyses || 10)
                      }
                    </span>
                  </div>
                  {usageData?.limits?.website_analyses !== -1 && (
                    <div className="relative">
                      <div className="w-full bg-slate-700 rounded-full h-3 shadow-inner">
                        <div 
                          className={`h-3 rounded-full transition-all duration-500 ${getUsageColor(usageInfo.percentage).bg} shadow-sm`}
                          style={{ width: `${Math.min(100, usageInfo.percentage)}%` }}
                        />
                      </div>
                      <div className="text-xs text-slate-500 mt-1 text-right">
                        {usageInfo.percentage.toFixed(1)}% used
                      </div>
                    </div>
                  )}
                </div>
                
                {/* API Calls */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-slate-400 font-medium">API Calls</span>
                    <span className="text-white font-bold">
                      {usageData?.current_period?.api_calls || 0} / {
                        usageData?.limits?.api_calls === -1 ? '∞' : (usageData?.limits?.api_calls || 50)
                      }
                    </span>
                  </div>
                  {usageData?.limits?.api_calls !== -1 && (
                    <div className="w-full bg-slate-700 rounded-full h-3 shadow-inner">
                      <div 
                        className={`bg-purple-500 h-3 rounded-full transition-all duration-500 shadow-sm`}
                        style={{ 
                          width: `${Math.min(100, ((usageData?.current_period?.api_calls || 0) / (usageData?.limits?.api_calls || 50)) * 100)}%` 
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Advanced Scans - Show only if available */}
                {(usageData?.limits?.advanced_scans || 0) > 0 && (
                  <div>
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-slate-400 font-medium">Advanced Scans</span>
                      <span className="text-white font-bold">
                        {usageData?.current_period?.advanced_scans || 0} / {
                          usageData?.limits?.advanced_scans === -1 ? '∞' : (usageData?.limits?.advanced_scans || 0)
                        }
                      </span>
                    </div>
                    {usageData?.limits?.advanced_scans !== -1 && (
                      <div className="w-full bg-slate-700 rounded-full h-3 shadow-inner">
                        <div 
                          className="bg-gradient-to-r from-pink-500 to-purple-500 h-3 rounded-full transition-all duration-500 shadow-sm"
                          style={{ 
                            width: `${Math.min(100, ((usageData?.current_period?.advanced_scans || 0) / (usageData?.limits?.advanced_scans || 1)) * 100)}%` 
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Daily Usage Chart */}
                {usageData?.daily_chart_data && usageData.daily_chart_data.length > 0 && (
                  <div className="pt-6 border-t border-slate-700">
                    <h4 className="text-sm font-bold text-slate-300 mb-4 flex items-center gap-2">
                      <LineChart className="w-4 h-4" />
                      Daily Activity (Last 7 Days)
                    </h4>
                    <div className="flex items-end justify-between h-20 gap-2">
                      {usageData.daily_chart_data.map((day, index) => {
                        const maxValue = Math.max(...usageData.daily_chart_data.map(d => d.analyses), 1);
                        const height = Math.max(6, (day.analyses / maxValue) * 60);
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center group">
                            <div 
                              className="w-full bg-gradient-to-t from-blue-500 to-purple-500 rounded-t-lg transition-all duration-300 group-hover:from-blue-400 group-hover:to-purple-400 shadow-lg"
                              style={{ height: `${height}px` }}
                              title={`${day.date}: ${day.analyses} analyses`}
                            />
                            <span className="text-xs text-slate-500 mt-2 font-medium">
                              {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Bottom Actions */}
                <div className="pt-6 border-t border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span>{usageData?.user_tier || 'free'} plan</span>
                    </div>
                    <Link 
                      to="/usage" 
                      className="flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-all group"
                    >
                      <Activity className="w-4 h-4" />
                      <span>View Details</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade CTA */}
        {currentTier === 'free' && (
          <div className="mt-12 relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-blue-900/40 to-purple-900/40 border border-blue-700/50 rounded-3xl p-10 text-center backdrop-blur-sm">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-2xl">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-3xl font-black text-white mb-4">
                Ready to Unlock Full AI Power?
              </h3>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Upgrade to Professional for unlimited analyses, advanced insights, priority processing, and exclusive features.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link 
                  to="/pricing"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-10 py-4 rounded-xl font-black text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-3"
                >
                  <Star className="w-6 h-6" />
                  Upgrade Now
                  <ChevronRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/feed"
                  className="border-2 border-slate-600 hover:border-slate-500 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3"
                >
                  <Globe className="w-6 h-6" />
                  Explore Community
                </Link>
              </div>
              
              <div className="mt-8 flex items-center justify-center gap-8 text-slate-400 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Unlimited analyses</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Priority processing</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Advanced insights</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compact Report Modal */}
      {selectedAnalysis && (
        <CompactReportModalWithShare
          analysis={selectedAnalysis}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAnalysis(null);
          }}
          currentUser={user}
          currentTier={currentTier}
          onLike={() => {}}
        />
      )}
    </div>
  );
};

export default UserDashboard;